import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalysis } from '../contexts/AnalysisContext';
import { useAuthFetch } from '../hooks/useAuthFetch';
import './Pages.css';

const STEPS = [
  { icon: '�', text: 'Uploading & storing resumes' },
  { icon: '🤖', text: 'Enriching & embedding with AI' },
  { icon: '🔍', text: 'Matching & ranking candidates' },
];

export const ProcessingPage: React.FC = () => {
  const navigate = useNavigate();
  const { state, setResults, setLoading, setError } = useAnalysis();
  const authFetch = useAuthFetch();
  const [activeStep, setActiveStep] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const hasStarted = useRef(false);

  // Animate the active step indicator
  useEffect(() => {
    const stepInterval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % STEPS.length);
    }, 3000);
    return () => clearInterval(stepInterval);
  }, []);

  // Elapsed timer
  useEffect(() => {
    const timer = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    const processResumes = async () => {
      try {
        if (!state.jobDescription || state.files.length === 0) {
          navigate('/');
          return;
        }

        const formData = new FormData();
        formData.append('jobDescription', state.jobDescription);
        state.files.forEach((file) => formData.append('resumes', file));

        const response = await authFetch('/api/rapid-match', {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const results = await response.json();
        setResults(results);
        setLoading(false);
        navigate(`/results/${state.sessionId}`);
      } catch (error) {
        console.error('Error processing resumes:', error);
        setError(error instanceof Error ? error.message : 'An error occurred');
        setLoading(false);
        setTimeout(() => navigate('/'), 3000);
      }
    };

    processResumes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatTime = (s: number) =>
    `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  if (state.error) {
    return (
      <div className="processing-page">
        <div className="processing-center">
          <div className="processing-card error-card">
            <div className="error-icon-large">⚠️</div>
            <h2>Processing Failed</h2>
            <p className="error-message">{state.error}</p>
            <p className="redirect-note">Redirecting back…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="processing-page">
      <div className="processing-center">
        <div className="processing-card">
          {/* Orbital spinner */}
          <div className="orbital-spinner">
            <div className="orbit orbit-1" />
            <div className="orbit orbit-2" />
            <div className="orbit orbit-3" />
            <div className="orbit-core">
              <span className="orbit-icon">{STEPS[activeStep].icon}</span>
            </div>
          </div>

          <h2 className="processing-title">Quick Screen in Progress</h2>

          {/* Step progress */}
          <div className="step-track">
            {STEPS.map((step, i) => (
              <div
                key={i}
                className={`step-item ${
                  i < activeStep ? 'done' : i === activeStep ? 'active' : ''
                }`}
              >
                <span className="step-bullet">
                  {i < activeStep ? '✓' : i + 1}
                </span>
                <span className="step-label">{step.text}</span>
              </div>
            ))}
          </div>

          {/* Meta info */}
          <div className="processing-meta">
            <span className="meta-badge">
              📁 {state.files.length} resume{state.files.length !== 1 ? 's' : ''}
            </span>
            <span className="meta-badge">
              ⏱ {formatTime(elapsed)}
            </span>
          </div>

          <p className="processing-hint">Uploading, enriching, and matching — usually 20–40 seconds</p>
        </div>
      </div>
    </div>
  );
};